import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Box, CircularProgress, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { useSearchPromosByToggle } from '~/hooks/apis/promo/useSearchPromosByToggle';
import useDebounced from '~/hooks/useDebounced';

import type { PromoMeta, SearchPromosByToggleQuery, SearchPromosByToggleResponse } from '~/services/PromoService';

export type PromoByToggleSearchResults = SearchPromosByToggleResponse['result'];
export type PromoByToggleEntity = SearchPromosByToggleQuery['entity'];
type Toggle = SearchPromosByToggleQuery['toggle'];

/** The complete, presentation-independent value produced by a toggle lookup. */
export type PromoByToggleSearch = {
  entity: PromoByToggleEntity;
  results: PromoByToggleSearchResults;
};

type Props = {
  promoMeta?: PromoMeta | null;
  /** Fixes the entity and hides the entity/toggle selectors for lookup-only use. */
  modeSet?: PromoByToggleEntity;
  /** Fixes the toggle and hides the entity/toggle selectors for lookup-only use. */
  toggleSet?: Toggle;
  /** Receives each completed lookup, or undefined while the lookup is changed or disabled. */
  onSearchChange?: (search?: PromoByToggleSearch) => void;
};

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to search by toggle.';
}

const NAME_QUERY_DEBOUNCE_MS = 300;

export default function PromoByToggle({ promoMeta, modeSet, toggleSet, onSearchChange }: Props) {
  const [entity, setEntity] = useState<PromoByToggleEntity>('promo');
  const [toggle, setToggle] = useState<Toggle>();
  const [query, setQuery] = useState('');
  const [hasFocusedNameQuery, setHasFocusedNameQuery] = useState(false);
  const debouncedQuery = useDebounced(query.trim(), NAME_QUERY_DEBOUNCE_MS);
  const selectedEntity = modeSet ?? entity;
  const isLookupOnly = modeSet !== undefined || toggleSet !== undefined;
  const toggleOptions = selectedEntity === 'promo' ? promoMeta?.promo_toggles : promoMeta?.voucher_toggles;
  // Entity changes before the effect below has selected its first valid toggle. Do not send the
  // old entity's toggle during that render: the API rejects entity/toggle combinations it cannot use.
  const selectedToggle = toggleSet ?? (toggleOptions?.some((option) => option.key === toggle) ? toggle : undefined);
  const lookupQuery = debouncedQuery || (hasFocusedNameQuery ? 'ALL_RESULTS' : '');
  const request = useMemo<SearchPromosByToggleQuery | null>(() => {
    if (!selectedToggle || !lookupQuery) {
      return null;
    }

    return { entity: selectedEntity, toggle: selectedToggle, query: lookupQuery, limit: 50 };
  }, [lookupQuery, selectedEntity, selectedToggle]);
  const { data, error, isFetching } = useSearchPromosByToggle(request);

  useEffect(() => {
    if (!toggleSet && !toggleOptions?.some((option) => option.key === toggle)) {
      setToggle(toggleOptions?.[0]?.key);
    }
  }, [toggle, toggleOptions, toggleSet]);

  useEffect(() => {
    onSearchChange?.();
  }, [onSearchChange, request]);

  useEffect(() => {
    if (data) {
      onSearchChange?.({ entity: selectedEntity, results: data.result });
    }
  }, [data, onSearchChange, selectedEntity]);

  function handleEntityChange(nextEntity: PromoByToggleEntity) {
    setEntity(nextEntity);
  }

  function handleToggleChange(nextToggle: string) {
    const matchingToggle = toggleOptions?.find((option) => option.key === nextToggle);
    if (matchingToggle) {
      setToggle(matchingToggle.key);
    }
  }

  return (
    <Box mt={4}>
      <Stack spacing={2}>
        {!isLookupOnly && (
          <Box>
            <Typography variant="h6">Promo and voucher toggle search</Typography>
            <Typography variant="body2" color="text.secondary">
              Find promos or vouchers using a configured toggle.
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          {!isLookupOnly && (
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                select
                label="Entity"
                value={selectedEntity}
                onChange={(event) => {
                  if (event.target.value === 'promo' || event.target.value === 'voucher') {
                    handleEntityChange(event.target.value);
                  }
                }}
                fullWidth
              >
                <MenuItem value="promo">Promo</MenuItem>
                <MenuItem value="voucher">Voucher</MenuItem>
              </TextField>
            </Grid>
          )}
          {!isLookupOnly && (
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Toggle"
                value={selectedToggle ?? ''}
                onChange={(event) => handleToggleChange(event.target.value)}
                fullWidth
              >
                {toggleOptions?.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.key}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: isLookupOnly ? 12 : 7 }}>
            <TextField
              label="Name query"
              value={query}
              onFocus={() => setHasFocusedNameQuery(true)}
              onChange={(event) => setQuery(event.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
        {isFetching && <CircularProgress size={24} aria-label="Searching promos and vouchers" />}
        {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
      </Stack>
    </Box>
  );
}
